import { lazy, Suspense, type ReactNode } from 'react'
import { useMe } from '../api/hooks'

const PendingDeletion = lazy(() => import('../views/PendingDeletion'))

/**
 * Puts the scheduled-for-deletion screen in place of whatever is below it, for as long as
 * `/api/me` says the account is pending (#365; SI-D11).
 *
 * A pending account signs in, and then every request but `/api/me` and "Keep my account" answers
 * 403 (`account_pending_deletion`), so nothing below this could draw: it wraps the whole shell
 * (`Layout`) and first run (`router.tsx`), the two places an authenticated page can mount, and
 * those are not mounted at all while the screen shows. It cannot be dismissed from inside: the
 * screen has two controls, and neither one leads to the app. The way out is "Keep my account",
 * which makes `/api/me` stop saying pending, and this gives way when it does.
 *
 * **Unknown is not pending.** While `/api/me` has not answered, or failed, the children render, as
 * `RequireFirstRun` already does: holding the whole shell back for a request answered in a few
 * milliseconds would cost every ordinary cold load a blank screen to spare the rare one that meets
 * this (`Layout`'s own doc names that trade). The price is paid by the pending account: the shell
 * may draw for one round trip, and its first requests answer 403 before the screen replaces it.
 * Nothing it asks for can succeed in that window, so nothing can be changed by it.
 */
export function PendingDeletionGate({ children }: { children: ReactNode }) {
  const { data } = useMe()
  if (data?.pending_deletion) {
    return (
      <Suspense fallback={null}>
        <PendingDeletion pending={data.pending_deletion} />
      </Suspense>
    )
  }
  return <>{children}</>
}
